import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Target, Wrench, Trophy } from "lucide-react";
import { FRAMEWORK } from "@/lib/content";

export const FrameworkInteractive = () => {
  const [active, setActive] = useState(0);
  const stage = FRAMEWORK[active];

  return (
    <div className="grid gap-8 lg:grid-cols-[0.9fr_1.4fr] lg:gap-14" data-testid="framework-interactive">
      {/* Stage selector */}
      <div className="flex flex-col gap-3">
        {FRAMEWORK.map((s, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            data-testid={`framework-stage-${i}`}
            className={`group relative flex items-center gap-4 rounded-2xl border p-4 text-left transition-all duration-300 ${
              active === i
                ? "border-orange-500/40 bg-orange-500/5"
                : "border-white/8 bg-white/[0.02] hover:border-white/20"
            }`}
          >
            <div
              className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl font-head text-xl font-extrabold transition-all ${
                active === i ? "grad-accent text-[#1a0f00]" : "bg-white/5 text-zinc-400 group-hover:text-white"
              }`}
            >
              {s.letter}
            </div>
            <div>
              <div className={`font-head text-base font-bold ${active === i ? "text-white" : "text-zinc-300"}`}>
                {s.title}
              </div>
              <div className="text-xs text-zinc-500">{s.tagline}</div>
            </div>
          </button>
        ))}
      </div>

      {/* Stage detail */}
      <div className="relative overflow-hidden rounded-[1.75rem] border border-white/10 bg-[#17171A] p-8 sm:p-10">
        <div className="hatch-diag absolute inset-0 opacity-40" />
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="mb-8 flex items-center gap-4">
              <span className="font-head text-7xl font-extrabold leading-none grad-text">{stage.letter}</span>
              <div>
                <div className="kicker">Stage {active + 1} / 5</div>
                <h3 className="font-head text-2xl font-bold text-white">{stage.title}</h3>
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-3">
              <FlowCard icon={<Target className="h-5 w-5" />} label="Customer Need" text={stage.need} tone="slate" />
              <FlowCard icon={<Wrench className="h-5 w-5" />} label="Pixel-Hatch Support" text={stage.support} tone="cyan" />
              <FlowCard icon={<Trophy className="h-5 w-5" />} label="Business Outcome" text={stage.outcome} tone="teal" />
            </div>

            <div className="mt-8 hidden items-center justify-between text-xs font-medium text-zinc-500 sm:flex">
              <span>Need</span>
              <ArrowRight className="h-4 w-4 text-orange-500" />
              <span>Support</span>
              <ArrowRight className="h-4 w-4 text-orange-500" />
              <span>Outcome</span>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

const FlowCard = ({ icon, label, text, tone }) => {
  const tones = {
    slate: "text-zinc-400",
    cyan: "text-orange-400",
    teal: "text-amber-400",
  };
  return (
    <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-5">
      <div className={`mb-3 flex items-center gap-2 ${tones[tone]}`}>
        {icon}
        <span className="text-[11px] font-semibold uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-sm leading-relaxed text-zinc-300">{text}</p>
    </div>
  );
};
